import { useEffect, useState } from 'react'
import { Modal, Input, Button, Message, Spin } from '@arco-design/web-react'
import { IconLock, IconSafe } from '@arco-design/web-react/icon'
import {
  SecureVerificationService,
  type VerificationMethods,
} from '@/services/secureVerification'
import type { SecurityProof } from '@/api/verify'

type VerifyMethod = '2fa' | 'password' | 'passkey'

interface SecureVerifyModalProps {
  visible: boolean
  title?: string
  /** 敏感操作的 scope，例如 channel.key.read */
  scope: string
  /** 与 scope 绑定的上下文，例如 { channel_id: 3 } */
  context?: Record<string, unknown>
  onCancel: () => void
  /** 验证成功回调，参数为后端签发的凭据（重试时放到 X-Security-Proof） */
  onSuccess: (proof: SecurityProof) => void
}

/**
 * 敏感操作的二次验证弹窗：按后端下发的可用方式渲染 2FA / 密码 / Passkey。
 */
export default function SecureVerifyModal({
  visible,
  title = '安全验证',
  scope,
  context,
  onCancel,
  onSuccess,
}: SecureVerifyModalProps) {
  const [methods, setMethods] = useState<VerificationMethods | null>(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<VerifyMethod>('2fa')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (!visible) {
      setCode('')
      setPassword('')
      return
    }
    setLoading(true)
    SecureVerificationService.checkAvailableVerificationMethods(scope)
      .then((res) => {
        setMethods(res)
        if (res.has2FA) setMode('2fa')
        else if (res.hasPassword) setMode('password')
        else if (res.hasPasskey && res.passkeySupported) setMode('passkey')
      })
      .finally(() => setLoading(false))
  }, [visible, scope])

  const handleVerify = async () => {
    setLoading(true)
    try {
      const proof = await SecureVerificationService.verify({ method: mode, scope, context, code, password })
      Message.success('验证成功')
      onSuccess(proof)
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '验证失败')
    } finally {
      setLoading(false)
    }
  }

  const canUse2FA = methods?.has2FA
  const canUsePassword = methods?.hasPassword
  const canUsePasskey = methods?.hasPasskey && methods?.passkeySupported
  const noMethod = methods && !canUse2FA && !canUsePassword && !canUsePasskey

  return (
    <Modal title={title} visible={visible} onCancel={onCancel} footer={null} maskClosable={false}>
      {loading && !methods ? (
        <div className="py-10 flex justify-center">
          <Spin />
        </div>
      ) : noMethod ? (
        <div className="py-6 text-sm text-gray-500">
          当前账号没有可用于该操作的验证方式，请先在「个人设置」中启用 2FA 或 Passkey。
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            {canUse2FA && (
              <Button
                type={mode === '2fa' ? 'primary' : 'outline'}
                icon={<IconLock />}
                onClick={() => setMode('2fa')}
              >
                2FA
              </Button>
            )}
            {canUsePassword && (
              <Button type={mode === 'password' ? 'primary' : 'outline'} onClick={() => setMode('password')}>
                登录密码
              </Button>
            )}
            {canUsePasskey && (
              <Button
                type={mode === 'passkey' ? 'primary' : 'outline'}
                icon={<IconSafe />}
                onClick={() => setMode('passkey')}
              >
                Passkey
              </Button>
            )}
          </div>

          {mode === '2fa' && (
            <div className="space-y-2">
              <div className="text-sm text-gray-600">请输入 6 位动态验证码或备用码</div>
              <Input
                value={code}
                onChange={setCode}
                placeholder="验证码"
                onPressEnter={handleVerify}
              />
            </div>
          )}

          {mode === 'password' && (
            <div className="space-y-2">
              <div className="text-sm text-gray-600">请输入当前账号的登录密码</div>
              <Input.Password
                value={password}
                onChange={setPassword}
                placeholder="登录密码"
                onPressEnter={handleVerify}
              />
            </div>
          )}

          {mode === 'passkey' && (
            <div className="text-sm text-gray-600">点击下方按钮触发浏览器 Passkey 验证。</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" loading={loading} onClick={handleVerify}>
              验证
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
